import React, { useEffect, useState, useContext } from 'react';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import HeaderComponent from './HeaderComponent';
import { apiService, setupInterceptors } from '../services/apiService';
import { AuthContext } from '../context/AuthContext';
import { DataTable } from 'primereact/datatable';
import { confirmDialog } from 'primereact/confirmdialog';
import { Column } from 'primereact/column';
import '../styles/Home.css';

const PanelCompras = ({ toast }) => {
    const { logoutUser } = useContext(AuthContext);
    const [productos, setProductos] = useState([]);
    const [selectedProducto, setSelectedProducto] = useState(null);
    const [carrito, setCarrito] = useState([]);

    const [ proveedores, setProveedores ] = useState([])
    const [ selectedProveedor, setSelectedProveedor ] = useState(null)

    useEffect(() => {
        setupInterceptors(logoutUser);
        getProductos();
        getProveedores();
    }, []);

    const getProductos = async () => {
        try {
            const response = await apiService.fetchData('productos/');
            const productosList = response.data.map(producto => ({
                id: producto.id,
                categoria_nombre: producto.categoria_nombre,
                codigo: producto.codigo,
                nombre: producto.nombre,
                proveedor_nombre: producto.proveedor_nombre,
                cantidad_disponible: producto.cantidad_disponible,
                precio_producto: producto.precio_producto,
                codigo_producto: producto.codigo_producto
            }));
            setProductos(productosList);
        } catch (error) {
            console.error('Error al obtener datos:', error.response ? error.response.data : error.message);
        }
    };

    const getProveedores = async () => {
        try {
            const response = await apiService.fetchData('proveedores/');
            const proveedoresList = response.data.map(proveedor => ({
                id: proveedor.id,
                nombre_contacto: proveedor.nombre_contacto,
            }));
            setProveedores(proveedoresList);
        } catch (error) {
            console.error('Error al obtener datos:', error.response ? error.response.data : error.message);
        }
    };

    const agregarAlCarrito = () => {
        if (selectedProducto) {
            const productoEnCarrito = carrito.find(p => p.id === selectedProducto.id);
            if (productoEnCarrito) {
                toast.current.show({ severity: 'warn', summary: 'Advertencia', detail: 'Este producto ya está en el carrito', life: 3000 });
                return; // Salir de la función si el producto ya está en el carrito
            }
            setCarrito([...carrito, { 
                ...selectedProducto, 
                nombre: `${selectedProducto.nombre} - ${selectedProducto.proveedor_nombre}`,
                cantidad: 1, 
                descuento: 0, 
                precio_costo: 0, // Inicializado por el usuario
                precio_venta: selectedProducto.precio_producto, // Inicializado por el usuario
                margen_ganancia: 0, // Se calculará automáticamente
                total: 0
            }]);
            setSelectedProducto(null); // Limpiar el Dropdown y el filtro
        }
    };


    // Validación de solo números en cantidad, precio y descuento
    const handleNumericInput = (value) => {
        const numericValue = value.replace(/[^0-9.]/g, '');
        return numericValue;
    };


    const actualizarCantidad = (productoId, cantidad) => {
        const numericCantidad = handleNumericInput(cantidad);
        const nuevoCarrito = carrito.map(producto => {
            if (producto.id === productoId) {
                const totalConCantidad = (producto.precio_costo * numericCantidad);
                return { ...producto, cantidad: numericCantidad, total: totalConCantidad.toFixed(2) };
            }
            return producto;
        });
        setCarrito(nuevoCarrito);
    };

    const actualizarPrecioCosto = (productoId, precioCosto) => {
        const numericPrecioCosto = handleNumericInput(precioCosto);
        const nuevoCarrito = carrito.map(producto => {
            if (producto.id === productoId) {
                const totalConCosto = (numericPrecioCosto * producto.cantidad); // Calcular total basado en cantidad y nuevo precio de costo
                const margenGanancia = (producto.precio_venta - numericPrecioCosto) / numericPrecioCosto * 100;
                return { 
                    ...producto, 
                    precio_costo: numericPrecioCosto, 
                    total: totalConCosto.toFixed(2), // Actualizar total
                    margen_ganancia: margenGanancia.toFixed(2) // Margen en porcentaje
                };
            }
            return producto;
        });
        setCarrito(nuevoCarrito);
    };

    const actualizarPrecioVenta = (productoId, precioVenta) => {
        const numericPrecioVenta = handleNumericInput(precioVenta);
        const nuevoCarrito = carrito.map(producto => {
            if (producto.id === productoId) {
                const margenGanancia = (numericPrecioVenta - producto.precio_costo) / producto.precio_costo * 100;
                return { 
                    ...producto, 
                    precio_venta: numericPrecioVenta, 
                    margen_ganancia: margenGanancia.toFixed(2) // Margen en porcentaje
                };
            }
            return producto;
        });
        setCarrito(nuevoCarrito);
    };

    const removerDelCarrito = (productoId) => {
        const nuevoCarrito = carrito.filter(producto => producto.id !== productoId);
        setCarrito(nuevoCarrito);
    };

    const calcularImporteTotal = () => {
        return carrito.reduce((total, producto) => total + parseFloat(producto.total), 0).toFixed(2);
    };

    const validarCamposCarrito = () => {
        for (const producto of carrito) {
            if (!producto.cantidad || !producto.precio_costo || !producto.precio_venta) {
                return false; // Hay al menos un campo vacío
            }
        }
        return true; // Todos los campos están llenos
    };
    
    const confirmarReposicionStock = () => {
        if(selectedProveedor === null){
            toast.current.show({ severity: 'warn', summary: 'Advertencia', detail: 'Debe Seleccionar un Proveedor para avanzar!', life: 3000 });
            return;
        }

        if (carrito.length > 0) {
            if (!validarCamposCarrito()) {
                toast.current.show({ severity: 'warn', summary: 'Advertencia', detail: 'Todos los campos deben estar completos!', life: 3000 });
                return; // Salir de la función si hay campos vacíos
            }

            confirmDialog({
                message: '¿Está seguro de que desea confirmar la compra?',
                header: 'Confirmación',
                icon: 'pi pi-info-circle',
                acceptLabel: "Confirmar",
                accept: () => {
                    const data = {
                        "carrito": carrito,
                        "importe_total": carrito.reduce((total, producto) => total + parseFloat(producto.total), 0).toFixed(2),
                        "proveedor": selectedProveedor
                    }
                    postCompraStock(data)
                },
                reject: () => {
                    console.log('Confirmación rechazada');
                }
            });
        } else {
            toast.current.show({ severity: 'warn', summary: 'Advertencia', detail: 'Debe agregar productos al carrito!', life: 3000 });
        }
    };

    const postCompraStock = async (data) => {
        try {
            const response = await apiService.postData('compras_stock/', data);

            toast.current.show({severity:'success', summary: 'Solicitud Exitosa', detail:'Compra creada exitosamente!', life: 2000});

            setCarrito([])

        } catch (error) {
            console.log(error);
            const errorMsg = error.response?.data?.msg 
                ? error.response.data.msg.slice(0, 300)  // Limita a 300 caracteres
                : error.message.slice(0, 300);           // También limita error.message
            
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: errorMsg,
                life: 3000
            });
            }
    };

    const formatearMoneda = (value) => {
        if (isNaN(value)) return value; // Verificar si el valor es un número
        return value.toLocaleString('es-AR', { style: 'currency', currency: 'ARS' });
    };


    return (
        <>
            <HeaderComponent title={"Panel de Compras"} />
            <div className='col-12 ml-6' style={{ minWidth: "95%", maxWidth: "95%" }}>
                <div className='ml-2' style={{ fontSize: "20px" }}>
                    <p>Bienvenid@ al Panel de Compras</p>
                </div>

                <div>
                    <div>
                        <h3>Seleccione Proveedor</h3>
                    </div>
                    <Dropdown
                        value={selectedProveedor}
                        options={proveedores}
                        onChange={(e) => setSelectedProveedor(e.value)}
                        optionLabel="nombre_contacto"
                        filter
                        showClear
                        placeholder="Selecciona un proveedor"
                        style={{ width: '20vw' }}
                    />
                </div>

                <div>
                    <h3>Carrito de Compras</h3>
                </div>
                {/* Dropdown con filtro para seleccionar el producto */}
                <div className="mb-4 p-inputgroup">
                    <Dropdown
                        value={selectedProducto}
                        options={productos}
                        onChange={(e) => setSelectedProducto(e.value)}
                        optionLabel="codigo_producto"
                        filter
                        showClear
                        placeholder="Selecciona un producto"
                        style={{ width: '300px' }}
                        itemTemplate={(option) => (
                            <div>
                                {option.codigo} - <strong>{option.nombre}</strong> - {option.proveedor_nombre}
                            </div>
                        )}
                        valueTemplate={(option) => 
                            option ? (
                                <div>
                                    {option.codigo} - <strong>{option.nombre}</strong> - {option.proveedor_nombre}
                                </div>
                            ) : (
                                <span>Selecciona un producto</span>
                            )
                        }

                    />
                    <Button label="Agregar" icon="pi pi-shopping-cart" onClick={agregarAlCarrito} disabled={!selectedProducto} />
                </div>

                {/* Carrito de Compras */}
                <div className='carrito'>
                    <DataTable value={carrito} responsiveLayout="scroll">
                        <Column style={{textAlign:"center" }} headerClassName='header-table-style' alignHeader={"center"} field="codigo" header="Código" />
                        <Column style={{textAlign:"center" }} headerClassName='header-table-style' alignHeader={"center"} field="categoria_nombre" header="Categoría" />
                        <Column style={{textAlign:"center" }} headerClassName='header-table-style' alignHeader={"center"} field="nombre" header="Nombre del Producto" />
                        <Column style={{textAlign:"center" }} headerClassName='header-table-style' alignHeader={"center"}
                            header="Cantidad"
                            body={(rowData) => (
                                <InputText
                                    value={rowData.cantidad}
                                    onChange={(e) => actualizarCantidad(rowData.id, e.target.value)}
                                    type="text"
                                    style={{ textAlign:"center", width:"7vw" }}
                                    pattern="\d*"
                                />
                            )}
                        />
                        <Column style={{textAlign:"center" }} headerClassName='header-table-style' alignHeader={"center"}
                            header="Precio de Costo"
                            body={(rowData) => (
                                <InputText
                                    value={rowData.precio_costo}
                                    onChange={(e) => actualizarPrecioCosto(rowData.id, e.target.value)}
                                    type="text"
                                    style={{ textAlign:"center", width:"7vw" }}
                                    pattern="\d*"
                                />
                            )}
                        />
                        <Column style={{textAlign:"center" }} headerClassName='header-table-style' alignHeader={"center"}
                            header="Precio de Venta"
                            body={(rowData) => (
                                <InputText
                                    value={rowData.precio_venta}
                                    onChange={(e) => actualizarPrecioVenta(rowData.id, e.target.value)}
                                    type="text"
                                    style={{ textAlign:"center", width:"7vw" }}
                                    pattern="\d*"
                                />
                            )}
                        />
                        <Column 
                            style={{ textAlign: "center" }} 
                            headerClassName='header-table-style' 
                            alignHeader={"center"}
                            body={(rowData) => (
                                <p style={{ color: rowData.margen_ganancia > 0 ? "green" : "black", fontWeight:600 }}>
                                    {rowData.margen_ganancia} %
                                </p>
                            )}
                            header="Marg. Ganancia" 
                        />
                        <Column style={{ textAlign: "center" }} headerClassName='header-table-style' alignHeader={"center"}
                            header="Total"
                            body={(rowData) => (
                                <span>{rowData.total}</span>
                            )}
                        />
                        <Column style={{ textAlign: "center" }} headerClassName='header-table-style' alignHeader={"center"}
                            body={(rowData) => (
                                <Button  icon="pi pi-trash" severity='danger' onClick={() => removerDelCarrito(rowData.id)} />
                            )}
                        />
                    </DataTable>
                    <div style={{ display:"flex", justifyContent:"center" }}>
                        <h3>Total: $ {formatearMoneda(calcularImporteTotal())}</h3>
                    </div>
                    <div style={{ display:"flex", justifyContent:"center" }}>
                        <Button label='Finalizar Compra' icon='pi pi-cart-plus' severity='warning' size='small' onClick={() => confirmarReposicionStock()} />
                    </div>
                </div>
            </div>
        </>
    );
}

export default PanelCompras;
